/**
 * Tells whether the last history step was already animated by the browser — iOS Safari's (and in-app
 * browsers') edge swipe back/forward, Chrome's predictive back. Those draw their own page slide, so the
 * app must not slide again on top of it. Buttons (the app's back arrow, the browser's arrows) are not
 * animated by the browser, so the app's own slide still runs for them.
 *
 * Signals: PopStateEvent.hasUAVisualTransition where the browser has it; else a touch that began at the
 * screen edge and never ended in the page (the browser took the gesture over) just before the popstate.
 */

const EDGE_PX = 30;
const GESTURE_MS = 5000;
const FLAG_MS = 1500;

let edgeTouchAt = 0;
let edgeTouchOpen = false;
let browserAnimatedAt = 0;
let installed = false;

export function installBrowserGestureWatch(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('touchstart', (e) => {
    const x = e.touches[0]?.clientX ?? EDGE_PX + 1;
    if (x <= EDGE_PX || x >= window.innerWidth - EDGE_PX) {
      edgeTouchAt = Date.now();
      edgeTouchOpen = true;
    } else {
      edgeTouchOpen = false;
    }
  }, { passive: true, capture: true });

  // A touch the page saw end was a tap or an in-page swipe, not the browser's gesture.
  window.addEventListener('touchend', () => { edgeTouchOpen = false; }, { passive: true, capture: true });

  window.addEventListener('popstate', (e) => {
    const ua = (e as PopStateEvent & { hasUAVisualTransition?: boolean }).hasUAVisualTransition === true;
    const swiped = edgeTouchOpen && Date.now() - edgeTouchAt < GESTURE_MS;
    edgeTouchOpen = false;
    browserAnimatedAt = ua || swiped ? Date.now() : 0;
  }, { capture: true });
}

/** True once, right after a history step the browser animated itself. */
export function consumeBrowserAnimated(): boolean {
  const animated = browserAnimatedAt !== 0 && Date.now() - browserAnimatedAt < FLAG_MS;
  browserAnimatedAt = 0;
  return animated;
}
